import React, { useState } from 'react';
import { DataMoverType } from '../../Enums/DataMoverType.enum';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import { joinClasses } from '../../../../Utils/className.util';
import styles from './DataMover.module.scss';
import Loading from '../../../../Components/Loading';
import InfiniteScroll from 'react-infinite-scroll-component';
import ScrollContainer from '../ScrollContainer';

interface DataMoverBaseProps<T> {
	data: T[];
	renderItem: (item: T) => React.ReactNode;
	buttonLabel?: string;
	hasMoreData: boolean;
	getNextPage: () => void;
}

interface DataMoverSingleProps<T> extends DataMoverBaseProps<T> {
	type: DataMoverType.SINGLE;
	onSubmit: (item: T) => void;
}

interface DataMoverMultipleProps<T> extends DataMoverBaseProps<T> {
	type: DataMoverType.MULTIPLE;
	onSubmit: (items: T[]) => void;
}

type DataMoverProps<T> = DataMoverSingleProps<T> | DataMoverMultipleProps<T>;

const SCROLL_CONTAINER_ID = 'dataMoverScrollContainer';

function DataMover<T>(props: DataMoverProps<T>) {
	const {
		data,
		type,
		renderItem,
		hasMoreData,
		getNextPage,
		buttonLabel = 'base.select',
	} = props;

	const isSingle = type === DataMoverType.SINGLE;

	const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
	const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(
		new Set(),
	);

	const handleToggle = (index: number) => {
		if (isSingle) {
			setSelectedIndex(index);
		} else {
			setSelectedIndexes(prev => {
				const next = new Set(prev);

				if (next.has(index)) {
					next.delete(index);
				} else {
					next.add(index);
				}

				return next;
			});
		}
	};

	const handleSubmit = () => {
		if (isSingle) {
			if (selectedIndex === null) return;
			(props as DataMoverSingleProps<T>).onSubmit(data[selectedIndex]);
		} else {
			const selected = data.filter((_, i) => selectedIndexes.has(i));
			(props as DataMoverMultipleProps<T>).onSubmit(selected);
		}
	};

	const isSubmitDisabled = isSingle
		? selectedIndex === null
		: selectedIndexes.size === 0;

	return (
		<div>
			<ScrollContainer
				wrapperId={SCROLL_CONTAINER_ID}
				className={styles.scrollContainer}>
				<InfiniteScroll
					style={{
						height: '100%',
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						minHeight: 0,
					}}
					className={styles.wrapper}
					dataLength={data.length}
					next={getNextPage}
					hasMore={hasMoreData}
					loader={<Loading />}
					scrollableTarget={SCROLL_CONTAINER_ID}>
					{data.map((item, index) => {
						const isChecked = isSingle
							? selectedIndex === index
							: selectedIndexes.has(index);
						return (
							<div
								key={index}
								className={joinClasses([
									styles.item,
									isChecked ? styles.active : '',
								])}
								onClick={() => handleToggle(index)}>
								<input
									className={styles.input}
									type={isSingle ? 'radio' : 'checkbox'}
									checked={isChecked}
									onChange={() => handleToggle(index)}
									onClick={e => e.stopPropagation()}
								/>
								<div className={styles.itemContent}>{renderItem(item)}</div>
							</div>
						);
					})}
				</InfiniteScroll>
			</ScrollContainer>
			<div className={styles.footer}>
				<Button
					onClick={handleSubmit}
					disabled={isSubmitDisabled}
					theme={Theme.GREEN}
					fitContent>
					{buttonLabel}
				</Button>
			</div>
		</div>
	);
}

export default DataMover;
